import {
  archivePuckPage,
  createPuckPage,
  deletePuckPage,
  duplicatePuckPage,
  getHomePage,
  getPageRoutes,
  listPuckPages,
  setHomePage,
  unsetHomePage,
  unpublishPuckPage,
  type PuckPageListItem,
} from "@/api/cms-puck/api";
import { SeoEditor } from "@/components/cms-puck/editor/SeoEditor";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import SearchBar from "@/components/ui/searchbar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listTemplates } from "@/api/cms-puck/templates";
import { useToast } from "@/hooks/use-toast";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Data } from "@measured/puck";
import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLink, Copy, EyeOff, FileText, Home, LayoutTemplate, MoreHorizontal, Pencil, Search, Trash2, Archive } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase, puckPageStatusLabel } from "@/lib/cms/cms-new-phrases";
import {
  isSystemModelPage,
  systemModelKindLabel,
} from "@/lib/cms-puck/system-model";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const PAGES_KEY = ["cms-puck", "pages"] as const;
const homeKey = ["cms-puck", "home"] as const;
const TEMPLATES_KEY = ["cms-puck", "templates"] as const;
const EMPTY_PAGE_DATA: Data = { content: [], root: { props: {} } };

type PagesSortColumn = "name" | "template" | "status" | "updatedAt";
type PagesSortDir = "asc" | "desc";

function PagesSortableHead({
  column,
  activeColumn,
  sortDir,
  onSort,
  title,
  children,
}: {
  column: PagesSortColumn;
  activeColumn: PagesSortColumn;
  sortDir: PagesSortDir;
  onSort: (column: PagesSortColumn) => void;
  title: string;
  children: ReactNode;
}) {
  const active = activeColumn === column;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 hover:text-slate-900"
        title={title}
      >
        {children}
        {active ? (
          sortDir === "asc" ? (
            <ArrowUp size={12} />
          ) : (
            <ArrowDown size={12} />
          )
        ) : (
          <ArrowUpDown size={12} className="opacity-50" />
        )}
      </button>
    </TableHead>
  );
}

/**
 * Vue « liste des pages » du CMS avancé (route `/cms/advanced`). Table des pages :
 * nom, statut, badge accueil, dernière modification + actions par ligne (éditer
 * la page, définir / retirer l'accueil, éditer le SEO, supprimer). L'édition Puck vit sur `/cms/advanced/:id`.
 */
const CMSAdvanced = () => {
  const cmsNew = useCmsNew();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { instanceData } = useAuth();
  const { defaultWebsiteLanguage } = useCmsWebsiteLanguages();

  // Liste complète : pages éditoriales + modèles système (catalogue, membre,
  // shell, recherche, abonnement…). Les exclusions restent pour les pickers
  // de liaison (menus, cibles de lien) — pas pour cet inventaire.
  const pagesQuery = useQuery({
    queryKey: PAGES_KEY,
    queryFn: () => listPuckPages(),
  });
  const homeQuery = useQuery({ queryKey: homeKey, queryFn: getHomePage });
  const homePageId = homeQuery.data?.homePageId ?? null;

  // Bibliothèque de modèles (les 9 proposés) — colonne Template.
  const templatesQuery = useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => listTemplates(),
  });
  /** Clé → libellé « Nom du set - titre du template » (uniquement la library). */
  const libraryTemplateLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of templatesQuery.data ?? []) {
      // Parité bibliothèque modèles : préfixe set en MAJUSCULES
      // (ex. « BASIQUE - Page d'accueil »).
      const setTitle = cmsNew(
        t.set.titleKey as `cms.new.${string}`,
        t.set.titleFr,
      ).toUpperCase();
      map.set(t.key, `${setTitle} - ${t.name}`);
    }
    return map;
  }, [templatesQuery.data, cmsNew]);

  // Ouvre la page publique (toujours fraîche : fetchs en no-store) → feedback
  // immédiat après publication. URL = frontUrl + slug langue par défaut (racine
  // si page d'accueil ; défaut non préfixé).
  const openPublic = async (page: PuckPageListItem) => {
    const frontUrl = (instanceData?.frontUrl ?? "").replace(/\/$/, "");
    if (!frontUrl) {
      toast({ title: cmsNew("cms.new.auto.title.url_du_site_non_configuree", "URL du site non configurée"), variant: "error" });
      return;
    }
    let path = "";
    if (homePageId !== page.id) {
      try {
        const routes = await getPageRoutes(page.id);
        const route =
          routes.find((r) => r.lang === defaultWebsiteLanguage) ?? routes[0];
        path = route?.slug ? `/${route.slug}` : "";
      } catch {
        /* pas de route → ouvre la racine */
      }
    }
    window.open(`${frontUrl}${path}`, "_blank", "noopener,noreferrer");
  };

  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState<"choose" | "blank">("choose");
  const [newName, setNewName] = useState("");

  const openCreate = () => {
    setCreateStep("choose");
    setNewName("");
    setCreateOpen(true);
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<PagesSortColumn>("updatedAt");
  const [sortDir, setSortDir] = useState<PagesSortDir>("desc");
  const [seoFor, setSeoFor] = useState<PuckPageListItem | null>(null);
  const [deleteFor, setDeleteFor] = useState<PuckPageListItem | null>(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  const toggleSort = (column: PagesSortColumn) => {
    if (sortColumn === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortDir(column === "updatedAt" ? "desc" : "asc");
  };

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      createPuckPage({ name, data: EMPTY_PAGE_DATA }),
    onSuccess: (page) => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      setCreateOpen(false);
      setNewName("");
      navigate(`/cms/advanced/${page.id}`);
    },
    onError: () => {
      toast({ title: cmsNew("cms.new.auto.title.echec_de_la_creation", "Échec de la création"), variant: "error" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePuckPage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      queryClient.invalidateQueries({ queryKey: homeKey });
      setDeleteFor(null);
      toast({ title: cmsNew("cms.new.auto.title.page_supprimee", "Page supprimée"), variant: "success", duration: 1600 });
    },
    onError: (err: unknown) => {
      const res = (
        err as { response?: { status?: number; data?: { message?: string } } }
      )?.response;
      toast({
        title:
          res?.status === 409
            ? (res.data?.message ?? cmsPhrase(cmsNew, "mediasDeleteBlocked"))
            : cmsPhrase(cmsNew, "mediasDeleteFailed"),
        variant: "error",
        duration: 3500,
      });
    },
  });

  // Helper de test : supprime TOUTES les pages (y compris en-tête / pied de page
  // et pages de parcours) pour repartir d'un CMS vierge avant un ré-import. On
  // liste sans exclusion puis on supprime une par une ; les échecs éventuels
  // (pages liées/bloquées) sont comptés sans interrompre le reste.
  const deleteAllMutation = useMutation({
    mutationFn: async () => {
      const all = await listPuckPages();
      let deleted = 0;
      let failed = 0;
      for (const p of all) {
        try {
          await deletePuckPage(p.id);
          deleted += 1;
        } catch {
          failed += 1;
        }
      }
      return { deleted, failed };
    },
    onSuccess: ({ deleted, failed }) => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      queryClient.invalidateQueries({ queryKey: homeKey });
      setDeleteAllOpen(false);
      toast({
        title: `${deleted} page(s) supprimée(s)${failed ? ` — ${failed} échec(s)` : ""}`,
        variant: failed ? "error" : "success",
        duration: 2500,
      });
    },
    onError: () => {
      toast({ title: "Échec de la suppression", variant: "error" });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: (id: string) => unpublishPuckPage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: cmsNew(
          "cms.new.auto.title.page_depubliee",
          "Page dépubliée (brouillon)",
        ),
        variant: "success",
        duration: 1600,
      });
    },
    onError: (err: unknown) => {
      const res = (
        err as { response?: { data?: { message?: string } } }
      )?.response;
      toast({
        title:
          res?.data?.message ??
          cmsNew(
            "cms.new.pages.unpublishFailed",
            "Échec de la dépublication",
          ),
        variant: "error",
      });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => archivePuckPage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({ title: cmsNew("cms.new.auto.title.page_archivee", "Page archivée"), variant: "success", duration: 1600 });
    },
    onError: (err: unknown) => {
      const res = (
        err as { response?: { status?: number; data?: { message?: string } } }
      )?.response;
      toast({
        title: res?.data?.message ?? cmsNew("cms.new.pages.archiveFailed", "Échec de l'archivage"),
        variant: "error",
      });
    },
  });

  // Définir / retirer la page d'accueil (une seule par tenant : définir une
  // autre page remplace l'accueil courant).
  const homeMutation = useMutation({
    mutationFn: (page: PuckPageListItem) =>
      homePageId === page.id ? unsetHomePage() : setHomePage(page.id),
    onSuccess: (data) => {
      queryClient.setQueryData(homeKey, data);
      // Le slug racine ("") devient (in)valide → routes à rafraîchir (SeoEditor).
      queryClient.invalidateQueries({ queryKey: ["cms-puck", "routes"] });
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: data.homePageId
          ? cmsNew("cms.new.seo.homeSet", "Page définie comme accueil")
          : cmsNew("cms.new.seo.homeUnset", "Page d'accueil retirée"),
        variant: "success",
        duration: 1600,
      });
    },
    onError: () => {
      toast({ title: cmsNew("cms.new.auto.title.echec_de_la_mise_a_jour_de_laccueil", "Échec de la mise à jour de l'accueil"), variant: "error" });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => duplicatePuckPage(id),
    onSuccess: (page) => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: cmsNew("cms.new.auto.title.page_dupliquee", "Page dupliquée"),
        description: cmsNew("cms.new.auto.description.les_traductions_ont_ete_copiees", "Les traductions ont été copiées."),
        variant: "success",
        duration: 2000,
      });
      navigate(`/cms/advanced/${page.id}`);
    },
    onError: () => {
      toast({ title: cmsNew("cms.new.auto.title.echec_de_la_duplication", "Échec de la duplication"), variant: "error" });
    },
  });

  const submitCreate = () => {
    const name = newName.trim();
    if (!name) return;
    createMutation.mutate(name);
  };

  const pages = pagesQuery.data ?? [];
  const filteredPages = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = q
      ? pages.filter((p) => p.name.toLowerCase().includes(q))
      : [...pages];

    const templateLabel = (page: PuckPageListItem) => {
      if (isSystemModelPage(page)) return systemModelKindLabel(page);
      if (!page.sourceTemplateKey) return "";
      return libraryTemplateLabelByKey.get(page.sourceTemplateKey) ?? "";
    };

    const cmpText = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: "base" });

    list.sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case "name":
          cmp = cmpText(a.name || "", b.name || "");
          break;
        case "template":
          cmp = cmpText(templateLabel(a), templateLabel(b));
          break;
        case "status":
          cmp = cmpText(
            puckPageStatusLabel(cmsNew, a.status),
            puckPageStatusLabel(cmsNew, b.status),
          );
          break;
        case "updatedAt":
          cmp =
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

    return list;
  }, [
    pages,
    searchQuery,
    sortColumn,
    sortDir,
    libraryTemplateLabelByKey,
    cmsNew,
  ]);

  const sortTitle = cmsNew("cms.new.auto.title.trier", "Trier");

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.auto.title.pages", "Pages")}
      subtitle={cmsNew(
        "cms.new.pages.listSubtitle",
        "Toutes les pages du site, y compris les modèles système (catalogue, recherche, membre…). Les liaisons de parcours se gèrent dans les onglets Parcours.",
      )}
      actions={
        <div className="flex items-center gap-2">
          {pages.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => setDeleteAllOpen(true)}
            >
              <Trash2 size={15} className="mr-1.5" />
              Tout supprimer (test)
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate("/cms/advanced/import-ancien-site")}
          >
            <LayoutTemplate size={15} className="mr-1.5" />
            {cmsNew("cms.new.import.title", "Importer mon ancien site")}
          </Button>
          <Button size="sm" onClick={openCreate}>
            {cmsPhrase(cmsNew, "newPageTitle")}
          </Button>
        </div>
      }
    >
      <div className="px-6 py-4">
        <p className="mb-4 flex flex-wrap gap-4 text-sm text-slate-600">
          <Link
            to="/cms/advanced/catalogue"
            className="font-medium text-slate-800 underline"
          >
            {cmsNew("cms.new.pages.manageCatalogTemplates", "Gérer les templates catalogue")} →
          </Link>
          <Link
            to="/cms/advanced/parcours-membre"
            className="font-medium text-slate-800 underline"
          >
            {cmsNew("cms.new.pages.manageMemberFlow", "Gérer le parcours membre")} →
          </Link>
          <Link
            to="/cms/advanced/selection-rubrique"
            className="font-medium text-slate-800 underline"
          >
            {cmsNew(
              "cms.new.pages.manageSelectionRubrique",
              "Gérer le modèle fiche rubrique",
            )}{" "}
            →
          </Link>
        </p>
        {pages.length > 0 && (
          <div className="mb-4 max-w-sm">
            <SearchBar
              search={searchQuery}
              onChangeSearch={setSearchQuery}
              placeholder={cmsNew("cms.new.auto.placeholder.rechercher_une_page", "Rechercher une page…")}
            />
          </div>
        )}
        {pagesQuery.isLoading ? (
          <p className="py-12 text-center text-sm text-slate-400">
            {cmsNew("cms.new.pages.loading", "Chargement…")}
          </p>
        ) : filteredPages.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            {pages.length === 0 ? (
              <>
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50 text-sky-600">
                  <FileText size={26} />
                </span>
                <p className="text-base font-semibold text-slate-900">
                  {cmsNew("cms.new.pages.emptyTitle", "Aucune page pour le moment")}
                </p>
                <p className="max-w-md text-sm text-slate-500">
                  {cmsNew(
                    "cms.new.pages.emptySubtitle",
                    "Importe automatiquement tout ton ancien site pour démarrer avec ton contenu, ou commence de zéro.",
                  )}
                </p>
                <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => navigate("/cms/advanced/import-ancien-site")}
                  >
                    <LayoutTemplate size={15} className="mr-1.5" />
                    {cmsNew("cms.new.import.title", "Importer mon ancien site")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={openCreate}>
                    {cmsNew("cms.new.templates.createPage", "Créer une page")}
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500">
                {cmsNew("cms.new.pages.noSearchResults", "Aucune page ne correspond à la recherche.")}
              </p>
            )}
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
          <div className="rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <PagesSortableHead
                    column="name"
                    activeColumn={sortColumn}
                    sortDir={sortDir}
                    onSort={toggleSort}
                    title={sortTitle}
                  >
                    {cmsNew("cms.new.auto.text.nom", "Nom")}
                  </PagesSortableHead>
                  <PagesSortableHead
                    column="template"
                    activeColumn={sortColumn}
                    sortDir={sortDir}
                    onSort={toggleSort}
                    title={sortTitle}
                  >
                    {cmsNew("cms.new.templates.column", "Modèle")}
                  </PagesSortableHead>
                  <PagesSortableHead
                    column="status"
                    activeColumn={sortColumn}
                    sortDir={sortDir}
                    onSort={toggleSort}
                    title={sortTitle}
                  >
                    {cmsNew("cms.new.auto.text.statut", "Statut")}
                  </PagesSortableHead>
                  <PagesSortableHead
                    column="updatedAt"
                    activeColumn={sortColumn}
                    sortDir={sortDir}
                    onSort={toggleSort}
                    title={sortTitle}
                  >
                    {cmsNew(
                      "cms.new.auto.text.derniere_modification",
                      "Dernière modification",
                    )}
                  </PagesSortableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPages.map((page) => (
                  <TableRow
                    key={page.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/cms/advanced/${page.id}`)}
                  >
                    <TableCell className="font-medium">
                      <span className="flex flex-wrap items-center gap-2">
                        {page.name || cmsPhrase(cmsNew, "unnamedPage")}
                        {homePageId === page.id && (
                          <Badge variant="default" className="gap-1">
                            <Home size={12} /> {cmsPhrase(cmsNew, "homeBadge")}
                          </Badge>
                        )}
                        {/* Rôles système (catalogue / membre / recherche…) :
                            affichés dans la colonne Template, plus ici. */}
                      </span>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const isModel = isSystemModelPage(page);
                        // Modèles système → badge sky + libellé métier.
                        // Pages issues de la library (9 modèles) → « Set - titre ».
                        // Sinon (vierge / clé technique hors library) → pas de badge.
                        const templateLabel = isModel
                          ? systemModelKindLabel(page)
                          : page.sourceTemplateKey
                            ? (libraryTemplateLabelByKey.get(
                                page.sourceTemplateKey,
                              ) ?? null)
                            : null;
                        if (!templateLabel) return null;
                        const badge = (
                          <Badge
                            variant="secondary"
                            className={
                              isModel
                                ? "inline-flex flex-row items-center gap-1 border border-sky-300 bg-sky-50 text-xs font-medium text-sky-800 hover:bg-sky-50"
                                : "inline-flex flex-row items-center gap-1 text-xs"
                            }
                          >
                            <LayoutTemplate size={11} className="shrink-0" />
                            {templateLabel}
                          </Badge>
                        );
                        if (!isModel) return badge;
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">{badge}</span>
                            </TooltipTrigger>
                            <TooltipContent variant="info">
                              {cmsNew(
                                "cms.new.systemModel.templateColumnTooltip",
                                "Ceci est un modèle système, pas une page autonome : il sert de gabarit pour un type de contenu (en-tête, fiche rubrique, parcours…).",
                              )}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          page.status === "PUBLISHED"
                            ? "default"
                            : page.status === "ARCHIVED"
                              ? "outlined"
                              : "secondary"
                        }
                      >
                        {puckPageStatusLabel(cmsNew, page.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {formatDate(page.updatedAt)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {page.status === "PUBLISHED" && (
                            <DropdownMenuItem onClick={() => void openPublic(page)}>
                              <ExternalLink size={14} className="mr-2" />
                              {cmsNew("cms.new.pages.viewAction", "Voir")}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(`/cms/advanced/${page.id}`)
                            }
                          >
                            <Pencil size={14} className="mr-2" />
                            {cmsPhrase(cmsNew, "editPage")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={homeMutation.isPending}
                            onClick={() => homeMutation.mutate(page)}
                          >
                            <Home size={14} className="mr-2" />
                            {homePageId === page.id
                              ? cmsNew("cms.new.seo.homeRemove", "Retirer l'accueil")
                              : cmsNew("cms.new.seo.homeDefine", "Définir comme accueil")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setSeoFor(page)}>
                            <Search size={14} className="mr-2" />
                            {cmsNew("cms.new.pages.editSeo", "Éditer la SEO")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => duplicateMutation.mutate(page.id)}
                          >
                            <Copy size={14} className="mr-2" />
                            {cmsNew("cms.new.pages.duplicate", "Dupliquer la page")}
                          </DropdownMenuItem>
                          {page.status === "PUBLISHED" && (
                            <>
                              <DropdownMenuItem
                                onClick={() =>
                                  unpublishMutation.mutate(page.id)
                                }
                              >
                                <EyeOff size={14} className="mr-2" />
                                {cmsNew(
                                  "cms.new.pages.unpublish",
                                  "Dépublier (brouillon)",
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  archiveMutation.mutate(page.id)
                                }
                              >
                                <Archive size={14} className="mr-2" />
                                {cmsNew("cms.new.pages.archive", "Archiver")}
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600 focus:bg-red-50 focus:text-red-700 data-[highlighted]:bg-red-50 data-[highlighted]:text-red-700"
                            onClick={() => setDeleteFor(page)}
                          >
                            <Trash2 size={14} className="mr-2" />
                            {cmsPhrase(cmsNew, "delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </TooltipProvider>
        )}
      </div>

      {/* Création d'une page : choix du point de départ (vierge / modèle) */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) {
            setCreateStep("choose");
            setNewName("");
          }
        }}
      >
        <DialogContent className="max-w-lg">
          {createStep === "choose" ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {cmsNew("cms.new.templates.chooserTitle", "Comment veux-tu commencer ?")}
                </DialogTitle>
                <DialogDescription>
                  {cmsNew(
                    "cms.new.templates.chooserSubtitle",
                    "Choisis un point de départ pour ta nouvelle page.",
                  )}
                </DialogDescription>
              </DialogHeader>
              {/* Modèles poussés en priorité : carte « Depuis un modèle » en
                  vedette (badge Recommandé + nombre de modèles), « Page vierge »
                  reléguée en option secondaire dessous. */}
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setCreateOpen(false);
                    navigate("/cms/advanced/modeles");
                  }}
                  className="relative flex items-start gap-4 rounded-xl border-2 border-sky-500 bg-sky-50/50 p-5 text-left transition-colors hover:bg-sky-50"
                >
                  <span className="absolute right-3 top-3 rounded-full bg-sky-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                    {cmsNew("cms.new.templates.recommended", "Recommandé")}
                  </span>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky-500 text-white">
                    <LayoutTemplate size={22} />
                  </span>
                  <span className="flex flex-col gap-1 pr-16">
                    <span className="text-base font-semibold text-slate-900">
                      {cmsNew("cms.new.templates.fromTemplateCardTitle", "Depuis un modèle")}
                    </span>
                    <span className="text-sm leading-relaxed text-slate-600">
                      {cmsNew(
                        "cms.new.templates.fromTemplateCardDesc",
                        "Choisis une trame (accueil, artiste, article…) déjà mise en page, avec du contenu type prêt à adapter.",
                      )}
                    </span>
                    {(templatesQuery.data?.length ?? 0) > 0 && (
                      <span className="mt-1 text-xs font-medium text-sky-700">
                        {templatesQuery.data?.length}{" "}
                        {cmsNew("cms.new.templates.modelsAvailable", "modèles disponibles")}
                      </span>
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setCreateStep("blank")}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-slate-300 hover:bg-slate-50"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                    <FileText size={18} />
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-slate-900">
                      {cmsNew("cms.new.templates.blankCardTitle", "Page vierge")}
                    </span>
                    <span className="text-xs leading-relaxed text-slate-500">
                      {cmsNew("cms.new.templates.blankCardDesc", "Aucun contenu pré-rempli.")}
                    </span>
                  </span>
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{cmsPhrase(cmsNew, "newPageTitle")}</DialogTitle>
                <DialogDescription>
                  {cmsPhrase(cmsNew, "newPageDescription")}
                </DialogDescription>
              </DialogHeader>
              <Input
                autoFocus
                value={newName}
                placeholder={cmsNew("cms.new.auto.placeholder.ex_a_propos", "Ex. À propos")}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitCreate();
                }}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateStep("choose")}>
                  {cmsNew("cms.new.templates.back", "Retour")}
                </Button>
                <Button
                  disabled={!newName.trim() || createMutation.isPending}
                  onClick={submitCreate}
                >
                  {cmsPhrase(cmsNew, "create")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Éditeur SEO */}
      {seoFor && (
        <SeoEditor
          pageId={seoFor.id}
          pageName={seoFor.name}
          open={!!seoFor}
          onOpenChange={(open) => {
            if (!open) setSeoFor(null);
          }}
        />
      )}

      {/* Suppression */}
      <Dialog
        open={!!deleteFor}
        onOpenChange={(open) => {
          if (!open) setDeleteFor(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{cmsNew("cms.new.auto.text.supprimer_la_page", "Supprimer la page")}</DialogTitle>
            <DialogDescription>
              {cmsPhrase(cmsNew, "deletePageDescription")}{" "}
              « {deleteFor?.name || cmsPhrase(cmsNew, "unnamedPage")} »
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFor(null)}>
              {cmsPhrase(cmsNew, "cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteFor && deleteMutation.mutate(deleteFor.id)}
            >
              {cmsPhrase(cmsNew, "delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suppression totale (helper de test) */}
      <Dialog
        open={deleteAllOpen}
        onOpenChange={(open) => {
          if (!open && !deleteAllMutation.isPending) setDeleteAllOpen(false);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer toutes les pages ?</DialogTitle>
            <DialogDescription>
              Supprime définitivement <strong>toutes</strong> les pages du CMS
              avancé, y compris l'en-tête, le pied de page et les pages de
              parcours. À utiliser pour repartir de zéro avant un ré-import.
              L'ancien CMS n'est pas touché.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deleteAllMutation.isPending}
              onClick={() => setDeleteAllOpen(false)}
            >
              {cmsPhrase(cmsNew, "cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteAllMutation.isPending}
              onClick={() => deleteAllMutation.mutate()}
            >
              {deleteAllMutation.isPending
                ? "Suppression…"
                : "Tout supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CmsAdminShell>
  );
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default CMSAdvanced;
